import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@fly/core';
import {
  type FilterOptionLabel,
  MULTI_VALUE_FILTER_DELIMITER,
} from '@fly/shared';
import { Separator } from '@radix-ui/themes';

import { FlyCheckboxDropdown } from '../../../../FlyCheckboxDropdown';

export type FilterCheckboxGroupProps = {
  options: { value: string; label: FilterOptionLabel; icon?: string }[];
  value: string | undefined;
  onChange: (value: string) => void;
  onRemove: () => void;
  loading?: boolean;
};

function parseMultiValue(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(MULTI_VALUE_FILTER_DELIMITER).filter(Boolean);
}

export function FilterCheckboxGroup({
  options,
  value,
  onChange,
  onRemove,
  loading = false,
}: FilterCheckboxGroupProps) {
  const { t } = useTranslation();
  const selectedValues = parseMultiValue(value);

  const translatedOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.value,
        label: t(...opt.label),
        icon: opt.icon,
      })),
    [options, t]
  );

  const handleValueChange = (values: string[]) => {
    if (values.length === 0) onRemove();
    else onChange(values.join(MULTI_VALUE_FILTER_DELIMITER));
  };

  return (
    <div>
      <FlyCheckboxDropdown
        options={translatedOptions}
        value={selectedValues}
        onValueChange={handleValueChange}
        loading={loading}
      />
      {value && (
        <>
          <Separator size='4' className='my-fly-3' />
          <Button
            color='neutral'
            variant='ghost'
            className='w-full'
            size='2'
            onClick={onRemove}
          >
            {t('app.dashboard.table_actions.clear_selection', {
              defaultValue: 'Clear Selection',
            })}
          </Button>
        </>
      )}
    </div>
  );
}
